/**
 * EditableField — click-to-edit text/number field for the campaign canvas.
 *
 * Renders as plain text; on click it becomes an input. Committing (blur or
 * Enter) posts a canvas_edit for the given path via onCommit — the edit is
 * appended to conversation history, so the canvas replays identically on
 * reload and the assistant treats it as authoritative.
 */

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export interface EditableFieldProps {
  label?: string;
  value: string | number | undefined | null;
  path: string;
  onCommit: (path: string, value: string | number) => Promise<unknown>;
  readonly?: boolean;
  numeric?: boolean;
  multiline?: boolean;
  placeholder?: string;
  flashing?: boolean;
  className?: string;
  textClassName?: string;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  path,
  onCommit,
  readonly,
  numeric,
  multiline,
  placeholder = '—',
  flashing,
  className,
  textClassName,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (editing) {
      (multiline ? textareaRef.current : inputRef.current)?.focus();
    }
  }, [editing, multiline]);

  const startEdit = () => {
    if (readonly || saving) return;
    setDraft(value === undefined || value === null ? '' : String(value));
    setEditing(true);
  };

  const commit = async () => {
    setEditing(false);
    const trimmed = draft.trim();
    const original = value === undefined || value === null ? '' : String(value);
    if (trimmed === original || trimmed === '') return;

    let next: string | number = trimmed;
    if (numeric) {
      const parsed = Number(trimmed.replace(/[^0-9.]/g, ''));
      if (!Number.isFinite(parsed)) return;
      next = parsed;
    }
    setSaving(true);
    try {
      await onCommit(path, next);
    } catch {
      // Hook surfaces the error; field falls back to server value.
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      setEditing(false);
    }
  };

  const sharedInputClasses =
    'w-full rounded-btn border-0 bg-card px-3 py-2 text-[15px] text-ink shadow-ring-ink outline-none';

  return (
    <div className={cn('group', flashing && 'canvas-flash rounded', className)}>
      {label && (
        <span className="mb-1 block text-label uppercase text-ink-3">
          {label}
        </span>
      )}
      {editing ? (
        multiline ? (
          <textarea
            ref={textareaRef}
            value={draft}
            rows={2}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={onKeyDown}
            className={sharedInputClasses}
          />
        ) : (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={onKeyDown}
            className={sharedInputClasses}
          />
        )
      ) : (
        <span
          role={readonly ? undefined : 'button'}
          tabIndex={readonly ? undefined : 0}
          onClick={startEdit}
          onKeyDown={(e) => {
            if (!readonly && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              startEdit();
            }
          }}
          className={cn(
            'inline-block max-w-full text-[15px] text-ink',
            !readonly &&
              'cursor-text border-b border-dashed border-transparent hover:border-line rounded-sm',
            saving && 'opacity-60',
            textClassName,
          )}
          title={readonly ? undefined : 'Click to edit'}
        >
          {value === undefined || value === null || value === ''
            ? <span className="text-ink-3">{placeholder}</span>
            : String(value)}
          {!readonly && (
            <span className="ml-1 text-xs text-ink-3 opacity-0 group-hover:opacity-100">
              ✎
            </span>
          )}
        </span>
      )}
    </div>
  );
};
